import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ClientErrorLoggerService } from '../services';
import { ANALYTICS_ORG_ID_PROVIDER } from './analytics-org-id.provider';

/**
 * Global Error Handler for automatic error logging
 * 
 * This error handler automatically logs all uncaught errors and HTTP errors
 * to the analytics backend using the ClientErrorLoggerService.
 * 
 * @remarks
 * - Handles both client-side errors and HTTP errors
 * - Extracts component name from stack trace when possible
 * - Still logs to console for development purposes
 * - Prevents duplicate error logging
 * 
 * @example
 * ```typescript
 * // In app.config.ts
 * import { ErrorHandler } from '@angular/core';
 * import { GlobalErrorHandler } from 'analytics-logging-lib';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     { provide: ErrorHandler, useClass: GlobalErrorHandler },
 *     provideAnalyticsLoggingLib({ ... })
 *   ]
 * };
 * ```
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private injector = inject(Injector);
  private readonly _getOrgId = inject(ANALYTICS_ORG_ID_PROVIDER);

  handleError(error: Error | HttpErrorResponse): void {
    // Get the error logger service lazily to avoid circular dependency
    const errorLogger = this.injector.get(ClientErrorLoggerService);
    const orgId = this._getOrgId();

    // Determine error type and log accordingly
    if (error instanceof HttpErrorResponse) {
      // Skip 401s — expected for unauthenticated visitors, not an app error.
      // Skip errors from the analytics endpoint itself to avoid an infinite loop.
      if (error.status === 401 || error.url?.includes('/api/analytics/')) {
        console.warn('GlobalErrorHandler: skipping HTTP error (401 or analytics loop)', error.url);
        return;
      }

      // Server/HTTP error
      errorLogger.logError(
        new Error(`HTTP ${error.status}: ${error.message}`),
        {
          orgId,
          type: 'http',
          status: error.status,
          statusText: error.statusText,
          url: error.url,
          errorResponse: error.error
        }
      ).subscribe();
    } else {
      // Client-side error
      errorLogger.logError(error, {
        orgId,
        type: 'client',
        component: this.extractComponentName(error)
      }).subscribe();
    }

    // Still log to console for development
    console.error('Global error caught:', error);
  }

  /**
   * Try to extract component name from stack trace
   */
  private extractComponentName(error: Error): string {
    const stack = error.stack || '';
    const match = stack.match(/at\s+(\w+Component)/);
    return match ? match[1] : 'Unknown';
  }
}
